/**
 * ReplaceProgramDialog
 *
 * Single-client conflict confirmation (trust-layer item #3).
 *
 * Fires when a trainer assigns a Program to a client who already has a
 * different active Program. Surfaces:
 *   - the existing program's name + week-in-cycle / day index
 *   - days since last workout
 *   - the new program name being swapped in
 *
 * Pronoun-neutral copy (avoids she/he/they misgendering). Two options only:
 * Replace or Cancel. "Keep both" was intentionally omitted — the data model
 * supports one active assignment at a time.
 *
 * On confirm:
 *   1. UPDATE old assignment: status='replaced', updated_at=now()
 *   2. INSERT new assignment (caller-supplied callback handles step 2).
 *
 * Note: the prior assignment's status is written as 'replaced' (text column,
 * no enum/check constraint — verified against prod schema). Analytics that
 * filter by status='completed' will not see these rows, which is correct —
 * they were not completed.
 *
 * Used by AssignProgramDialog. The bulk dialogs use inline per-row warning
 * chips + a single bulk summary instead (less friction for celebratory flows).
 */

import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { differenceInCalendarDays, parseISO } from "date-fns";

export interface ActiveAssignmentSummary {
  /** Current assignment row id (the one being replaced). */
  assignmentId: string;
  /** Existing program name. */
  programName: string;
  /** ISO date the existing program started. */
  startedOn: string;
  /** Current day in the program cycle (1-indexed). */
  currentDay: number;
  /** Length of one cycle (e.g. 7 for a weekly program). */
  daysPerCycle: number;
  /** Trainer's intended total cycles, if cycle-based; null = non-cycled. */
  intendedCycles: number | null;
  /** ISO `completed_at` of the most recent workout log, null if never. */
  lastWorkoutAt: string | null;
}

interface ReplaceProgramDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientName: string;
  newProgramName: string;
  current: ActiveAssignmentSummary;
  /** Fires the actual replace (parent does the UPDATE+INSERT). */
  onConfirm: () => Promise<void> | void;
}

/**
 * Build the contextual line about cycle/day progress. Cycled programs read
 * "Week N of M"; non-cycled (intended_cycles null/<=1 with days_per_cycle
 * matching total) read "Day N".
 */
function progressLine(c: ActiveAssignmentSummary): string {
  const week = Math.max(1, Math.ceil(c.currentDay / Math.max(1, c.daysPerCycle)));
  if (c.intendedCycles && c.intendedCycles > 1) {
    return `Week ${week} of ${c.intendedCycles}`;
  }
  return `Day ${c.currentDay}`;
}

/**
 * Build the last-workout fragment. If the client has never logged a workout,
 * we drop the "X days ago" clause entirely so we don't ship a fake "0 days
 * ago" or "NaN days ago".
 */
function lastWorkoutFragment(lastWorkoutAt: string | null): string | null {
  if (!lastWorkoutAt) return null;
  const days = differenceInCalendarDays(new Date(), parseISO(lastWorkoutAt));
  if (days < 0) return null; // future date — shouldn't happen, fail-soft
  if (days === 0) return "last workout today";
  if (days === 1) return "last workout yesterday";
  return `last workout ${days} days ago`;
}

export function ReplaceProgramDialog({
  open,
  onOpenChange,
  clientName,
  newProgramName,
  current,
  onConfirm,
}: ReplaceProgramDialogProps) {
  const [confirming, setConfirming] = useState(false);

  const progress = progressLine(current);
  const lastWorkout = lastWorkoutFragment(current.lastWorkoutAt);

  // Pronoun-neutral body copy — refer to the program, not the client, in the
  // continuing clause so we never need she/he/they.
  const contextLine = lastWorkout
    ? `${current.programName} is in ${progress}, ${lastWorkout}.`
    : `${current.programName} is in ${progress} — hasn't started yet.`;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {clientName} is already on {current.programName}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>{contextLine}</p>
              <p>
                Switching to <span className="font-medium">{newProgramName}</span>{" "}
                will replace that. The {current.programName} progress gets archived,
                not lost.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={confirming}>
            Keep on {current.programName}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={confirming}>
            {confirming ? "Replacing…" : `Replace with ${newProgramName}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ReplaceProgramDialog;
